import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  previewIssue,
  useBookingPaymentAccounts,
  type Booking,
  type BookingGarment,
  type BookingWearer,
} from '@/api/bookings';
import { describeError } from '@/api/mutations';
import { ApiError } from '@/api/problem';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice } from '@/components/PageHeading';
import { GarmentImage } from '@/components/GarmentImage';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { useSplitParts } from '@/components/useSplitParts';
import { StatusBadge } from '@/components/StatusBadge';
import { ConditionPhotos } from './ConditionPhotos';
import { ScanBox } from './ScanBox';
import { isReadyToIssue } from '@core/bookings/tones';
import { formatAmount } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';
import { MoneyLine, MoneyLines } from './MoneyLine';
import { CounterBand } from './CounterBand';
import { Choice } from './Dialog';
import {
  handoverButton,
  handoverLeftOwing,
  handoverPartsProblem,
} from '@core/bookings/handoverMoney';
import { filledParts, fillOne, splitTotal, type SplitPart } from '@core/cash/split';
import { formatDate } from '@core/format/dates';
import { withReference } from '@core/units/referenceNumber';
import {
  accessoriesLeaving,
  allAccessories,
  canIssue,
  collectionTotals,
  defaultSelection,
  issuableGarments,
  issuePreviewInput,
  missingIssuePhotos,
  toggleWearer,
  wearerSelection,
  type SelectionState,
} from '@core/bookings/issue';

/**
 * Issuing garments at the counter.
 *
 * <p>
 * The highest-value moment in the flow to get right: money changes hands, the agreement is signed,
 * and the photographs taken here are the shop's only defence in a dispute six days later.
 * </p>
 *
 * <p>
 * <strong>The unit of the screen is the wearer, not the garment.</strong> A person collects their
 * whole outfit, so ticking Hiren takes all three of his items; individual garments can still be
 * unticked underneath. A wearer with nothing ready stays on the screen, greyed, with the reason —
 * filtering them out would let a clerk assume the system had lost them.
 * </p>
 *
 * <p>
 * <strong>Handing over takes the money with it</strong>, one button — "Hand over 2 · take ₹23,376" —
 * into the account boxes or Later, as on the phone (2026-10-22; boxes, split payments 2026-11-01).
 * Issuing still never takes money: the page sends the hand-over, then records a payment per account
 * through the ordinary payment, the amounts read off the booking the hand-over answered with
 * (`paymentsAfterIssueInParts`), so the GST taken is the invoice's own.
 * </p>
 */

export function IssueCounter({
  booking,
  onCancel,
  onIssue,
  mayTakeMoney = false,
  busy = false,
  currency = '',
  photoRequired = false,
}: {
  booking: Booking;
  /** The shop asks for a photograph of each garment going out (`Booking.Issue.RequirePhoto`). */
  photoRequired?: boolean;
  onCancel: () => void;
  /**
   * Given the garment ids going out, and whether the operator has knowingly waived the photograph
   * requirement. The caller owns the mutation and its errors.
   *
   * <p>
   * The acknowledgement is reported rather than assumed. §9 makes issue-and-return photographs the
   * primary dispute defence, and the checkbox below is the shop deciding to go without one for this
   * handover — a caller that always sends `true` has silently switched the guard off everywhere.
   * </p>
   */
  onIssue: (
    garmentIds: number[],
    acknowledgeMissingPhotos: boolean,
    accessoryIds: number[],
    /** The account boxes the money goes into, or null to take nothing now. */
    parts: readonly SplitPart[] | null,
  ) => void;
  /**
   * The reader handles the cash (`RentalOrder:ViewFinancials`) — the money is offered with the
   * hand-over only then; otherwise the button hands over and says who takes the money.
   */
  mayTakeMoney?: boolean;
  busy?: boolean;
  currency?: string;
}) {
  const [selected, setSelected] = useState<Set<number>>(() => defaultSelection(booking));
  const [acknowledged, setAcknowledged] = useState(false);
  const [later, setLater] = useState(false);

  // Everything ticked to start with — see `allAccessories`. What is sent is narrowed to the
  // garments actually going out, so unticking a wearer takes their brooches with them.
  const [accessories, setAccessories] = useState<Set<number>>(() => allAccessories(booking));

  // Lands on the first wearer's tick box, where Space toggles and Enter walks the list — the two
  // keys this counter is actually worked with.
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // Every garment on the booking, ready or not. The scan box matches against all of them on
  // purpose: "at the tailor" and "not on this booking" are different mistakes with different fixes.
  const everyGarment = booking.wearers.flatMap((wearer) => wearer.garments);

  // The GST this hand-over's invoice will raise, asked of the server's own invoice code for exactly
  // the garments ticked — the client never applies a rate. Without it a counter that collected
  // everything it showed left the customer owing the tax (decision log, 2026-10-05).
  const previewRequest = issuePreviewInput(selected);
  const preview = useQuery({
    queryKey: ['booking', booking.id, 'issue-preview', previewRequest?.garmentIds ?? []],
    queryFn: () => previewIssue(booking.id, previewRequest ?? {}),
    enabled: previewRequest !== null,
  });
  // A 400 is the refusal the hand-over itself would meet, so the button goes with it.
  const refused = preview.error instanceof ApiError && preview.error.status === 400;
  const working = previewRequest !== null && preview.isPending;

  const totals = collectionTotals(booking, preview.data?.addsToBalance ?? 0);
  const missingPhotos = missingIssuePhotos(booking, selected, photoRequired);
  // The money goes into account boxes (split payments): untouched, all of "Take now" sits in the
  // till's box and follows the figure as garments are ticked and the GST arrives; typing takes over.
  const accounts = useBookingPaymentAccounts(
    mayTakeMoney ? (booking.branchId ?? undefined) : undefined,
  );
  const { parts, change } = useSplitParts(accounts.data, (fresh) =>
    totals.total > 0 ? fillOne(fresh, totals.total) : fresh,
  );
  // Only what the boxes hold goes with the hand-over — empty boxes take nothing, as Later does.
  const taking = mayTakeMoney && totals.total > 0 && !later && filledParts(parts).length > 0;
  const partsProblem = taking ? handoverPartsProblem(totals.total, parts, currency) : null;
  const leftOwing = taking ? handoverLeftOwing(totals.total, parts, currency) : null;
  const blocked = selected.size === 0 || busy || refused || (missingPhotos > 0 && !acknowledged);

  const handOver = () =>
    onIssue(
      [...selected],
      acknowledged,
      accessoriesLeaving(booking, selected, accessories),
      taking ? parts : null,
    );

  return (
    // Enter moves to the next control and the last one hands focus to Issue — see
    // `enterKeyNavigation`. The wrapper exists for that handler: the commit button lives in the
    // heading's action slot, so a handler on the grid below would have nothing to hand the last
    // field to.
    <div ref={fields} {...enterKeyScope}>
      <CounterBand
        eyebrow={`${booking.documentNumber} · ${booking.customerName}`}
        title="Hand over"
        onBack={onCancel}
        trailing={
          <span className="text-[13px] text-white/85">
            {selected.size} of {booking.garmentCount} garments going out
          </span>
        }
      />

      <div className="grid gap-3 lg:grid-cols-[1fr_320px] lg:gap-4">
        <div className="flex flex-col gap-3 lg:gap-4">
          {refused && (
            <Notice tone="danger" title="These garments cannot be invoiced yet">
              {describeError(preview.error)}
            </Notice>
          )}

          {preview.isError && !refused && (
            <Notice tone="warning" title="The GST on this hand-over could not be worked out">
              The total below leaves it out, so the balance will rise once the garments go out.
            </Notice>
          )}

          {missingPhotos > 0 && (
            <Notice
              tone="warning"
              title={`${missingPhotos} garment${missingPhotos === 1 ? '' : 's'} going out without a photograph`}
            >
              Section 9 calls issue-and-return photographs the primary dispute defence. You can
              still issue — but a damage claim on an unphotographed garment is one the shop loses.
            </Notice>
          )}

          <Card flush>
            <div className="border-border-color flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Scan each garment out</p>
                <span className="text-default text-[12.5px]">
                  {selected.size} selected of {booking.garmentCount}
                </span>
              </div>

              {/* The heading has said this since the counter was built. This is the input that
                  makes it true. Scoped to the booking, so the answer to a tag from the next
                  booking on the rail is a refusal by name rather than a silent nothing. */}
              <ScanBox
                garments={everyGarment}
                eligible={(garment) => isReadyToIssue(garment.state)}
                disabled={busy}
                placeholder="Scan a garment out"
                ineligibleHint={(garment) =>
                  `${garment.itemName} is on this booking but ${humanizeEnum(
                    garment.state,
                  ).toLowerCase()} — it cannot go out yet.`
                }
                onMatch={(garment) => setSelected((was) => new Set(was).add(garment.id))}
              />
            </div>

            <div className="divide-border-color divide-y">
              {booking.wearers.map((wearer) => (
                <WearerBlock
                  key={wearer.id}
                  wearer={wearer}
                  state={wearerSelection(wearer, selected)}
                  selected={selected}
                  bookingId={booking.id}
                  accessories={accessories}
                  onToggleAccessory={(id) =>
                    setAccessories((was) => {
                      const next = new Set(was);

                      if (next.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                      }

                      return next;
                    })
                  }
                  onToggleWearer={() => setSelected(toggleWearer(wearer, selected))}
                  onToggleGarment={(id) =>
                    setSelected((was) => {
                      const next = new Set(was);

                      if (next.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                      }

                      return next;
                    })
                  }
                />
              ))}
            </div>
          </Card>

          <Card>
            <p className="mb-3 text-sm font-semibold text-gray-900">Before the garments leave</p>
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
                className="mt-0.5"
              />
              <span className="text-gray-900">
                Return date explained — <b>due back {booking.possessionToDate}</b>, and the
                accessories listed on the customer copy have to come back with the garments.
              </span>
            </label>
          </Card>
        </div>

        <div className="lg:sticky lg:top-4 lg:self-start">
          <Card>
            <p className="mb-2.5 text-sm font-semibold text-gray-900">Take now</p>

            <MoneyLines>
              <MoneyLine
                label="Rental still to pay"
                value={`${currency}${formatAmount(totals.rental)}`}
              />
              {working ? (
                <MoneyLine label="GST on this hand-over" value="…" />
              ) : totals.tax > 0 ? (
                <MoneyLine
                  label="GST on this hand-over"
                  hint="raised as they go out"
                  value={`${currency}${formatAmount(totals.tax)}`}
                />
              ) : null}
              {totals.deposit > 0 ? (
                <MoneyLine
                  label="Deposit"
                  hint="refundable"
                  value={`${currency}${formatAmount(totals.deposit)}`}
                  tone="brand"
                />
              ) : (
                <MoneyLine label="Deposit" hint="already held" value="—" />
              )}
              <MoneyLine
                label="Take now"
                value={`${currency}${formatAmount(totals.total)}`}
                total
              />
            </MoneyLines>

            {mayTakeMoney && totals.total > 0 ? (
              <div className="mt-3 flex flex-col gap-2.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <Choice
                    label="Take it now"
                    chosen={!later}
                    disabled={busy}
                    onChoose={() => setLater(false)}
                  />
                  <Choice
                    label="Later"
                    chosen={later}
                    disabled={busy}
                    onChoose={() => setLater(true)}
                  />
                </div>
                {!later && (
                  <>
                    {parts.length === 0 ? (
                      <p className="text-default mb-0 text-[12px]">
                        {accounts.isPending
                          ? 'Loading the accounts…'
                          : 'No account can take money at this branch. Add one in Cash and bank.'}
                      </p>
                    ) : (
                      <SplitBoxes parts={parts} onChange={change} direction="into" busy={busy} />
                    )}
                    <QuickAmounts
                      options={[
                        {
                          label: `All ${currency}${formatAmount(totals.total)}`,
                          amount: totals.total,
                        },
                      ]}
                      busy={busy}
                      onPick={(amount) => change((current) => fillOne(current, amount))}
                    />
                    {partsProblem ? (
                      <Notice tone="danger" title={partsProblem} />
                    ) : leftOwing ? (
                      <p className="text-default mb-0 text-[12px]">{leftOwing}</p>
                    ) : null}
                  </>
                )}
              </div>
            ) : totals.total > 0 ? (
              <p className="text-default mt-3 text-[12px]">
                Someone who handles the cash takes this.
              </p>
            ) : null}

            <Button
              variant="primary"
              data-enter-key-commit
              shortcut="save"
              // Blocked until the waiver is ticked when photographs are missing, so the server's
              // refusal is anticipated at the counter. It waits for the GST rather than naming a
              // pre-tax figure.
              disabled={blocked || (taking && working) || partsProblem !== null}
              onClick={handOver}
              className="mt-3 h-12 w-full justify-center text-[15px]"
            >
              {handoverButton({
                garments: selected.size,
                take: taking ? splitTotal(parts) : null,
                currency,
              })}
            </Button>

            <p className="text-default mt-3 text-[11.5px] leading-relaxed">
              {totals.deposit > 0
                ? 'Two receipts: one against the invoice, one for the deposit, which stays out of taxable value.'
                : 'The deposit was already taken and is not collected again.'}{' '}
              Due back {formatDate(booking.possessionToDate)}.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function WearerBlock({
  wearer,
  state,
  selected,
  bookingId,
  accessories,
  onToggleAccessory,
  onToggleWearer,
  onToggleGarment,
}: {
  wearer: BookingWearer;
  state: SelectionState;
  selected: ReadonlySet<number>;
  bookingId: number;
  accessories: ReadonlySet<number>;
  onToggleAccessory: (accessoryId: number) => void;
  onToggleWearer: () => void;
  onToggleGarment: (garmentId: number) => void;
}) {
  const servable = canIssue(wearer);
  const blocked = wearer.garments.filter((garment) => !issuableGarments(wearer).includes(garment));

  return (
    <section className={servable ? '' : 'opacity-60'}>
      <header className="bg-light flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5">
        <input
          type="checkbox"
          checked={state === 'all'}
          ref={(node) => {
            if (node) {
              // The half-state a person expects when some of an outfit is ticked.
              node.indeterminate = state === 'some';
            }
          }}
          disabled={!servable}
          onChange={onToggleWearer}
          aria-label={`Issue to ${wearer.name}`}
        />
        <span className="text-[13px] font-semibold text-gray-900">{wearer.name}</span>
        {wearer.relation && <span className="text-default text-[11.5px]">{wearer.relation}</span>}

        <span className="ms-auto">
          {servable ? (
            <StatusBadge tone="positive">
              {issuableGarments(wearer).filter((garment) => selected.has(garment.id)).length} of{' '}
              {issuableGarments(wearer).length} going
            </StatusBadge>
          ) : (
            <StatusBadge tone="warning">Nothing can go yet</StatusBadge>
          )}
        </span>
      </header>

      <ul className="divide-border-color divide-y">
        {wearer.garments.map((garment) => (
          <GarmentRow
            key={garment.id}
            garment={garment}
            issuable={!blocked.includes(garment)}
            checked={selected.has(garment.id)}
            bookingId={bookingId}
            accessories={accessories}
            onToggleAccessory={onToggleAccessory}
            onToggle={() => onToggleGarment(garment.id)}
          />
        ))}
      </ul>
    </section>
  );
}

function GarmentRow({
  garment,
  issuable,
  checked,
  bookingId,
  accessories,
  onToggleAccessory,
  onToggle,
}: {
  garment: BookingGarment;
  issuable: boolean;
  checked: boolean;
  bookingId: number;
  accessories: ReadonlySet<number>;
  onToggleAccessory: (accessoryId: number) => void;
  onToggle: () => void;
}) {
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-2.5 ps-8">
      <input
        type="checkbox"
        checked={checked && issuable}
        disabled={!issuable}
        onChange={onToggle}
        aria-label={`Issue ${garment.serialCode}`}
        className="mt-1"
      />

      {/* What the garment is supposed to look like, so the clerk can check the thing in their hand
          against the picture without leaving the counter. */}
      <GarmentImage url={garment.imageUrl} alt={garment.serialCode} size="sm" />

      <span className="min-w-0 flex-1">
        <span className="text-[13px] font-medium text-gray-900">{garment.itemName}</span>
        <span className="text-default block font-mono text-[11px]">
          {withReference(garment.serialCode, garment.referenceNumber)}
        </span>
        {/* Ticked, not printed. This used to render `accessories.filter(a => a.issued)` as text —
            which at the issue counter is always empty, because nothing has been issued yet. So the
            one screen that decides what left the shop showed nothing, and the deduction the return
            counter promises had no source. Each is on by default: the brooch goes with the sherwani,
            and unticking is the exception. */}
        {garment.accessories.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {garment.accessories.map((accessory) => (
              <label
                key={accessory.id}
                className={`flex items-center gap-1.5 text-[11.5px] ${
                  issuable ? 'cursor-pointer text-gray-900' : 'text-default'
                }`}
              >
                <input
                  type="checkbox"
                  checked={accessories.has(accessory.id)}
                  disabled={!issuable}
                  onChange={() => onToggleAccessory(accessory.id)}
                />
                {accessory.name}
                {accessory.quantity > 1 && ` ×${accessory.quantity}`}
              </label>
            ))}
          </span>
        )}
        {!issuable && (
          <span className="text-warning mt-0.5 block text-[11.5px]">
            {garment.alterationTailorName
              ? `At ${garment.alterationTailorName}${garment.alterationDueOn ? ` until ${garment.alterationDueOn}` : ''} — issue separately`
              : 'Cannot go yet — issue separately'}
          </span>
        )}
      </span>

      {/* This badge used to say "Add photo" and do nothing — there was nowhere to put one. The
          camera beside it is that sentence finally being actionable. */}
      <ConditionPhotos
        bookingId={bookingId}
        garmentId={garment.id}
        serialCode={garment.serialCode}
        phase="Out"
        disabled={!issuable}
      />
    </li>
  );
}
